import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  whiteboardAiService,
  summariseScene,
  ChatTurn,
} from '../services/whiteboardAiService';
import { specToElements, autoLayout } from '../lib/whiteboardAiSchema';
import '../styles/WhiteboardAiChat.css';

/**
 * Conversational diagram assistant docked beside the Excalidraw canvas.
 *
 * The user describes what they want; the model returns a diagram spec which is
 * expanded into Excalidraw elements and merged into the live scene.
 *
 * Edits are additive by design: existing elements are updated in place when the
 * model references them by id, but nothing is removed unless the user explicitly
 * asked. A misread instruction must never destroy work.
 */

interface Props {
  /** Excalidraw imperative API. Null until the canvas mounts. */
  excalidrawAPI: any;
  open: boolean;
  onClose: () => void;
  /** Called after a successful scene mutation so the parent can persist. */
  onSceneChanged?: () => void;
}

interface Message extends ChatTurn {
  id: string;
  /** Set on assistant turns that failed, to style them as an error. */
  error?: boolean;
  /** Warnings raised while expanding the spec (unknown icons, bad edges). */
  warnings?: string[];
}

const SUGGESTIONS = [
  'Draw a login flow with success and error paths',
  'Create an architecture diagram: web app, API, database, cache',
  'Make a mind map for Q3 product priorities',
  'Add a retry loop after the API call',
];

const WhiteboardAiChat: React.FC<Props> = ({ excalidrawAPI, open, onClose, onSceneChanged }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Keep the newest message in view as the transcript grows.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const applySpec = useCallback((spec: ReturnType<typeof autoLayout>): string[] => {
    if (!excalidrawAPI) return ['Canvas not ready.'];

    const existing = excalidrawAPI.getSceneElements() as any[];
    const positioned = autoLayout(spec);
    const { elements: generated, files, warnings } = specToElements(positioned);

    if (generated.length === 0) return warnings;

    // Register icon images before the elements that reference them, or
    // Excalidraw renders empty frames on the first paint.
    if (Object.keys(files).length > 0) {
      excalidrawAPI.addFiles(Object.values(files));
    }

    // Merge: the model may return an existing id to update that element in
    // place. Anything it doesn't mention is left untouched.
    const generatedById = new Map(generated.map(el => [el.id as string, el]));
    const merged = existing.map(el =>
      generatedById.has(el.id)
        ? { ...el, ...generatedById.get(el.id), version: (el.version ?? 1) + 1 }
        : el
    );
    const existingIds = new Set(existing.map(el => el.id));
    const additions = generated.filter(el => !existingIds.has(el.id as string));

    excalidrawAPI.updateScene({ elements: [...merged, ...additions] });

    // Frame the new content so the user sees the result immediately.
    if (additions.length > 0) {
      requestAnimationFrame(() => {
        try {
          excalidrawAPI.scrollToContent(additions as any, { fitToContent: true, animate: true });
        } catch {
          /* scrollToContent is best-effort — never block on it */
        }
      });
    }

    onSceneChanged?.();
    return warnings;
  }, [excalidrawAPI, onSceneChanged]);

  const send = useCallback(async (text: string) => {
    const instruction = text.trim();
    if (!instruction || busy) return;

    const userMsg: Message = { id: `u${Date.now()}`, role: 'user', content: instruction };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setBusy(true);

    try {
      const scene = excalidrawAPI
        ? summariseScene(excalidrawAPI.getSceneElements() as any[])
        : [];

      // History excludes the turn just added — it's passed as the instruction.
      const history: ChatTurn[] = messages.map(m => ({ role: m.role, content: m.content }));
      const { spec, message } = await whiteboardAiService.generateDiagram(
        instruction, scene, history,
      );

      let warnings: string[] = [];
      if (spec) warnings = applySpec(spec);

      setMessages(prev => [...prev, {
        id: `a${Date.now()}`,
        role: 'assistant',
        content: spec
          ? message
          : (message || "I couldn't turn that into a diagram. Try describing the boxes and how they connect."),
        warnings: warnings.length ? warnings : undefined,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: `e${Date.now()}`,
        role: 'assistant',
        content: err instanceof Error ? err.message : 'Something went wrong.',
        error: true,
      }]);
    } finally {
      setBusy(false);
    }
  }, [busy, excalidrawAPI, messages, applySpec]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  if (!open) return null;

  return (
    <aside className="wbai" aria-label="AI diagram assistant">
      <header className="wbai-header">
        <div className="wbai-header-title">
          <span className="wbai-badge" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
                 strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
              <rect x="6" y="6" width="12" height="12" rx="3" />
            </svg>
          </span>
          <div>
            <h3>AI Assistant</h3>
            <p>Describe a diagram and I'll draw it</p>
          </div>
        </div>
        <button className="wbai-close" onClick={onClose} aria-label="Close assistant">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </header>

      <div className="wbai-scroll" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="wbai-intro">
            <p className="wbai-intro-title">What should we draw?</p>
            <p className="wbai-intro-body">
              I can create flowcharts, architecture diagrams and mind maps — or add to
              what's already on the board.
            </p>
            <div className="wbai-suggestions">
              {SUGGESTIONS.map(s => (
                <button key={s} className="wbai-suggestion" onClick={() => send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map(m => (
          <div
            key={m.id}
            className={`wbai-msg ${m.role === 'user' ? 'is-user' : 'is-assistant'}${m.error ? ' is-error' : ''}`}
          >
            <div className="wbai-msg-body">{m.content}</div>
            {m.warnings && (
              <ul className="wbai-warnings">
                {m.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>
        ))}

        {busy && (
          <div className="wbai-msg is-assistant">
            <div className="wbai-typing" aria-label="Thinking">
              <span /><span /><span />
            </div>
          </div>
        )}
      </div>

      <div className="wbai-composer">
        <textarea
          ref={inputRef}
          className="wbai-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Describe a diagram, or ask for a change…"
          rows={2}
          disabled={busy}
        />
        <button
          className="wbai-send"
          onClick={() => send(input)}
          disabled={busy || !input.trim()}
          aria-label="Send"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="m22 2-7 20-4-9-9-4z" />
          </svg>
        </button>
      </div>
    </aside>
  );
};

export default WhiteboardAiChat;
